/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { Container, SvgGraphics } from '@tencent/alloy-renderer';
import type { DocumentElement } from '@tencent/alloy-editor';
import { DocSizeType, measureText } from '@tencent/alloy-editor';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { defaultValues } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/const';
import { RenderPostionType } from '@src/js/constant';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { CANVAS_CHART_COMPONENT_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { STLayoutMode } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { ChartEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/chart-edit-logic';
import type {
    IParagraph,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import type {
    IEditorProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import type { IFrameInfo } from '@editor/src/page/index/components/canvas/element-wrapper/utils';
import type {
    IChartTitle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import {
    ChartElementType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { setElementStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style-render';
import { getOriginStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';
import {
    ITextBodyStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { TextEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor';

export interface ChartTitleComponentProps {
    frame: IFrameInfo;
    model: IChartModel;
    factoryOptions: InterfaceFactoryOptions<IChartModel>;
}

const DEFAULT_RECT = {
    left: undefined,
    top: defaultValues.TITLE_MARGIN,
};
interface TitleRect { top: number; left: number; width: number; height: number  }
export class ChartTitleComponent extends Container {
    public rect: TitleRect;
    public textEditor: TextEditor;
    public factoryOptions: InterfaceFactoryOptions<IChartModel>;
    // 标题背景，用来承载填充、边框和特效
    private titleBg: SvgGraphics;

    private chartFrame: IFrameInfo;
    private chartTitleModel: IChartTitle;
    private chartModel: IChartModel;
    private paragraphList: IParagraph[];
    private textBodyStyle: ITextBodyStyle | undefined;

    constructor(props: ChartTitleComponentProps) {
        super();
        this.id = `${CANVAS_CHART_COMPONENT_ID_PREFIX}-title`;
        this.chartModel = props.model;
        this.factoryOptions = props.factoryOptions;
        this.chartTitleModel = props.model.title!;
        this.updateProps(props);

        this.onMouseDown((e) => {
            this.chartEditLogic?.handleChartTitleMouseDown(e);
        });
    }

    public updateProps(props: ChartTitleComponentProps) {
        const { frame: chartFrame, model } = props;
        this.chartFrame = chartFrame;
        this.chartModel = props.model;
        this.factoryOptions = props.factoryOptions;
        this.chartTitleModel = props.model.title!;

        const chartTitleProperty = model.chartProperty.chartTitle;
        this.paragraphList = chartTitleProperty?.getParagraphList() || [];
        this.textBodyStyle = chartTitleProperty?.getTextBodyStyle({ alignItems: 'flex-start' });
        this.rect = this.getTitleRect();

        if (!this.textEditor) {
            this.textEditor = new TextEditor(this.getEditorProps(), { ...this.factoryOptions });
            this.addChild(this.textEditor.editorContainer);

            // 只在canvas region下绑定，不然会导致 list下也绑定，导致editor实例错乱
            if (!this.factoryOptions.fromScale) {
                model.setEditorComponent(this.textEditor);
            }
        } else {
            this.updateTextEditor();
        }
        this.createOrUpdateTextBg();
        // 裁剪超出区域
        if (this.textEditor.isEditing) {
            this.clipPath = '';
        } else {
            this.setTitleClipPath();
        }
    }

    /**
     * 获取真正的title rect区域
     */
    public getRealTitleRect(): TitleRect {
        const { left, top } = this.rect;
        if (!this.textEditor.editor) return { left, top, width: 0, height: 0 };
        const { width, height } = this.textEditor.editor.getLayoutSize();
        const { maxTitleWidth, maxTitleHeight } = this;
        return { left, top, width: Math.min(Math.ceil(width), maxTitleWidth),
            height: Math.min(Math.ceil(height), maxTitleHeight) };
    }

    public dispose(): void {
        super.dispose();

        // 移除model中editor组件
        if (!this.factoryOptions.fromScale) { // 和注册逻辑对应
            this.factoryOptions.model.setEditorComponent(null);
            // 如果处于选中态，移除选中态
            this.chartModel.removeIfSelected(ChartElementType.TITLE);
        }
    }

    private createOrUpdateTextBg() {
        const { left, top, width, height } = this.getRealTitleRect();
        const path = `M ${left},${top} l ${width},0 l 0,${height} l${-width} 0 z`;
        if (!this.titleBg) {
            this.titleBg = new SvgGraphics(path);
            this.addChildAt(this.titleBg, 0);
        } else {
            this.titleBg.path = path;
        }
        const props = this.chartModel.chartProperty.chartTitle?.props;
        setElementStyle(this.titleBg, getOriginStyle(props), this.factoryOptions);
    }

    /**
     * 超出最大高度，用来截掉文字超出区域显示
     * @private
     */
    private setTitleClipPath() {
        const { left, top, width, height } = this.getRealTitleRect();
        this.clipPath = `M ${left},${top} l ${width},0 l 0,${height} l${-width} 0 z`;
    }

    private updateTextEditor() {
        this.textEditor.updateEditorProps(this.getEditorProps());

        this.chartEditLogic?.onTitleEditorUpdate();
    }

    private getEditorProps(): IEditorProps {
        // TODO 排版层支持最大高度省略

        // 图表标题的点击分两步：
        // 1. 先点击标题，激活"选中态"，出现边框，hover cursor变成 text。
        //     此时可以在侧边栏中进行一些编辑
        // 2. 当hover text再点击时，可以直接进行文字编辑
        return {
            modelKey: this.factoryOptions.model.key,
            modelKeySuffix: `-title-${this.factoryOptions.region}`,
            editable: this.checkCurrentEditable() && !!this.isSelected,
            selectable: this.checkCurrentEditable() && !!this.isSelected,
            onChange: (value) => {
                this.chartEditLogic?.handleTitleChange(value);
            },
            onTextChange: this.onTextChange,
            cursor: this.getCursor(),
            shouldUpdate: true,
            paragraphList: this.paragraphList,
            // @ts-ignore
            textRect: this.rect,
            textBodyStyle: this.textBodyStyle,
            getApplication: this.factoryOptions.getApplication?.bind(this),
            /**
             * 文本样式发生变更。
             * @param style
             */
            onStyleChange: (style) => {
                const { model, fromScale } = this.factoryOptions;
                if (!fromScale && model) {
                    model.selectionStyles = style.style;
                }
            },
            onEnterEditing: () => {
                this.clipPath = '';
            },
            onExitEditing: () => {
                // 异步一下，是为了处理撤销失焦的时候，获取clip rect不对问题
                Promise.resolve().then(() => {
                    this.setTitleClipPath();
                });
            },
        };
    }

    private onTextChange = () => {
        const documentElement = this.textEditor.editor.getValue();
        // 测量后，可以拿到内容宽度
        documentElement.docSizeType = DocSizeType.WrapContent;

        const { width: titleWidth, height: titleHeight } = this.getMeasuredTitleSize(documentElement);
        this.rect = this.getActualTextRect(titleWidth, titleHeight);

        const { paragraphList } = this.textEditor.getData();
        this.textEditor.updateEditorProps({
            ...this.getEditorProps(),
            paragraphList,
        });

        this.chartEditLogic?.handleTitleTextChange();
    };

    private get chartEditLogic(): ChartEditLogic | undefined {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions) as ChartEditLogic;
    }

    private get isSelected() {
        return this.chartModel.isElementSelected(ChartElementType.TITLE);
    }

    private getCursor() {
        if (this.factoryOptions.region === RenderPostionType.PLAYER) {
            return 'auto';
        }

        return this.isSelected ? 'text' : 'move';
    }

    private getTitleRect(): TitleRect {
        const documentElement = EditorDataModel.convertFromTextModel({
            textBodyStyle: this.textBodyStyle,
            paragraphList: this.paragraphList,
        });
        // 测量后，可以拿到内容宽度
        documentElement.docSizeType = DocSizeType.WrapContent;

        const { width: titleWidth, height: titleHeight } = this.getMeasuredTitleSize(documentElement);
        return this.getActualTextRect(titleWidth, titleHeight);
    }

    private getMeasuredTitleSize(documentElement: DocumentElement): { width: number, height: number } {
        const { maxTitleWidth, maxTitleHeight } = this;
        const size = measureText(documentElement, { width: maxTitleWidth, height: maxTitleHeight });
        return { width: Math.min(Math.ceil(size.width), maxTitleWidth),
            height: Math.min(Math.ceil(size.height), maxTitleHeight) };
    }

    private get maxTitleWidth() {
        return Math.ceil(this.chartFrame.width * 0.8);
    }

    private get maxTitleHeight() {
        return Math.ceil(this.chartFrame.height * 0.5);
    }

    // 当有layout时，会动态的调整一下位置，防止标题超出
    private getActualTextRect(titleWidth: number, titleHeight: number) {
        const { layout } = this.chartTitleModel;
        const { width: chartWidth, height: chartHeight } = this.chartFrame;

        const rect: TitleRect = {
            top: DEFAULT_RECT.top,
            left: (this.chartFrame.width - titleWidth) / 2,
            width: titleWidth,
            height: titleHeight,
        };

        if (layout) {
            // TODO 完整解析layout类型和数据
            if (layout.leftMode === STLayoutMode.edge) {
                rect.left = chartWidth * layout.left!;

                // rect.left是相对图表左边界的相对位置，当标题右边界超出了chartFrame右边界进行偏移
                if (rect.left + titleWidth >= chartWidth) {
                    rect.left = chartWidth - titleWidth;
                }
            }

            if (layout.topMode === STLayoutMode.edge) {
                rect.top = chartHeight * layout.top!;

                if (rect.top + titleHeight >= chartHeight) {
                    rect.top = chartHeight - titleHeight;
                }
            }
        }

        return rect;
    }

    private checkCurrentEditable(): boolean {
        const { model } = this.factoryOptions;
        return model.currentEditState === PPTEditorStateType.EDITABLE;
    }
}
